import { apiPost, apiGet } from '../ApiClient';

/**
 * Build a POS Invoice document payload from cart + profile state.
 * Does NOT send to server — returns the plain object.
 *
 * @param {Object} params
 * @param {Object} params.profile - { name, company, warehouse, currency, ... }
 * @param {Object} params.profileData - Full profile data from get_pos_profile_data
 * @param {string|null} params.customer - Customer name
 * @param {Array} params.items - Cart items [{ item_code, item_name, qty, rate, uom }]
 * @param {Array} params.payments - [{ mode_of_payment, amount }]
 * @returns {Object} POS Invoice document payload
 */
export function buildInvoicePayload({
  profile,
  profileData,
  customer,
  items,
  payments,
  quote,
  loyaltyRedemption,
}) {
  const quotedItems = quote?.items?.length ? quote.items : items;
  const loyalty = quote?.loyalty || {};

  return {
    doctype: 'POS Invoice',
    is_pos: 1,
    pos_profile: profile.name,
    company: profile.company,
    customer: customer || profileData?.customer || profile?.customer,
    currency: profile.currency || profileData?.currency,
    selling_price_list: profileData?.selling_price_list || profile.selling_price_list,
    set_warehouse: profileData?.warehouse || profile.warehouse,
    posting_date: new Date().toISOString().split('T')[0],
    posting_time: new Date().toTimeString().split(' ')[0],
    update_stock: 1,
    items: quotedItems.map((item) => ({
      item_code: item.item_code,
      item_name: item.item_name,
      qty: item.qty,
      rate: item.rate,
      price_list_rate: item.price_list_rate || item.rate,
      discount_percentage: item.discount_percentage || 0,
      discount_amount: item.discount_amount || 0,
      pricing_rules: Array.isArray(item.pricing_rules)
        ? item.pricing_rules.join(',')
        : item.pricing_rules,
      is_free_item: item.is_free_item || 0,
      uom: item.uom || item.stock_uom || 'Nos',
    })),
    payments: payments.map((p) => ({
      mode_of_payment: p.mode_of_payment,
      amount: p.amount,
    })),
    redeem_loyalty_points: loyaltyRedemption?.enabled ? 1 : 0,
    loyalty_points: loyaltyRedemption?.enabled ? loyaltyRedemption.points || 0 : 0,
    loyalty_program: loyaltyRedemption?.loyalty_program || loyalty.loyalty_program,
    loyalty_amount: loyalty.loyalty_amount || 0,
    loyalty_redemption_account: loyalty.loyalty_redemption_account || loyalty.expense_account,
    loyalty_redemption_cost_center: loyalty.loyalty_redemption_cost_center || loyalty.cost_center,
  };
}

/**
 * Create a POS Invoice on the server (Draft status).
 * Frappe computes totals, taxes, debit_to, etc. automatically.
 *
 * @param {Object} payload - Document payload from buildInvoicePayload()
 * @returns {Promise<Object>} Created document with name, grand_total, etc.
 */
export async function createPosInvoice(payload) {
  const res = await apiPost('resource/POS Invoice', payload);
  return res?.data;
}

/**
 * Submit a POS Invoice (Draft → Submitted).
 * Must pass the full doc (including `modified`) to avoid TimestampMismatchError.
 *
 * @param {Object} doc - Full document object returned from createPosInvoice()
 * @returns {Promise<Object>} Submitted document
 */
export async function submitPosInvoice(doc) {
  const res = await apiPost('method/frappe.client.submit', {
    doc: JSON.stringify(doc),
  });
  return res?.message;
}

/**
 * Create and submit a POS Invoice in one flow.
 * Returns the submitted invoice document.
 */
export async function createAndSubmitInvoice({
  profile,
  profileData,
  customer,
  items,
  payments,
  quote,
  loyaltyRedemption,
}) {
  const payload = buildInvoicePayload({
    profile,
    profileData,
    customer,
    items,
    payments,
    quote,
    loyaltyRedemption,
  });
  const created = await createPosInvoice(payload);
  const submitted = await submitPosInvoice(created);
  return submitted || created;
}

/**
 * Fetch a POS Invoice by name.
 */
export async function fetchInvoice(name) {
  const res = await apiGet(`resource/POS Invoice/${name}`);
  return res?.data;
}

// ---------------------------------------------------------------------------
// Returns
// ---------------------------------------------------------------------------

/**
 * Search past POS Invoices (for returns).
 * Uses the ERPNext point_of_sale whitelisted method.
 *
 * @param {string} searchTerm - Invoice name or customer name
 * @param {string} status - 'Paid', 'Consolidated', etc.
 * @param {number} limit - Max results
 * @returns {Promise<Array>} Past invoices
 */
export async function fetchPastOrders({ searchTerm = '', status = '', limit = 20 }) {
  const res = await apiPost(
    'method/erpnext.selling.page.point_of_sale.point_of_sale.get_past_order_list',
    { search_term: searchTerm, status, limit },
  );
  return res?.message || [];
}

/**
 * Generate a return document from an existing POS Invoice.
 * Uses ERPNext's make_sales_return which creates a pre-filled return
 * with negated quantities and payments.
 *
 * @param {string} invoiceName - Original POS Invoice name
 * @returns {Promise<Object>} Return document (not yet saved)
 */
export async function makeReturnDoc(invoiceName) {
  const res = await apiPost(
    'method/erpnext.accounts.doctype.pos_invoice.pos_invoice.make_sales_return',
    { source_name: invoiceName },
  );
  return res?.message;
}

function getReturnPaymentAmounts(doc) {
  const invoiceTotal = doc.rounded_total || doc.grand_total || 0;
  const baseInvoiceTotal = doc.base_rounded_total || doc.base_grand_total || invoiceTotal * (doc.conversion_rate || 1);

  return {
    amount: invoiceTotal <= 0 ? invoiceTotal : -invoiceTotal,
    baseAmount: baseInvoiceTotal <= 0 ? baseInvoiceTotal : -baseInvoiceTotal,
  };
}

function applyReturnPaymentAmounts(doc) {
  const { amount, baseAmount } = getReturnPaymentAmounts(doc);
  const firstPayment = (doc.payments || [])[0] || {};
  doc.payments = [
    {
      ...firstPayment,
      mode_of_payment: firstPayment.mode_of_payment || 'Cash',
      amount,
      base_amount: baseAmount,
    },
  ];

  doc.paid_amount = amount;
  doc.base_paid_amount = baseAmount;
  doc.change_amount = 0;
  doc.base_change_amount = 0;
  doc.write_off_amount = 0;
  doc.base_write_off_amount = 0;
  doc.outstanding_amount = 0;

  return doc;
}

/**
 * Normalize a generated POS return document before inserting it.
 *
 * ERPNext validates POS return payment totals during insert, so the draft
 * payload must already carry a negative paid amount matching the return total.
 */
function normalizeReturnDoc(returnDoc) {
  const doc = { ...returnDoc };

  return applyReturnPaymentAmounts(doc);
}

/**
 * Create and submit a return POS Invoice.
 * Takes the return doc from makeReturnDoc, creates it, then submits.
 *
 * @param {Object} returnDoc - Return document from makeReturnDoc()
 * @returns {Promise<Object>} Submitted return invoice
 */
export async function submitReturn(returnDoc) {
  const normalizedReturnDoc = normalizeReturnDoc(returnDoc);
  // Insert the return document
  const created = await createPosInvoice(normalizedReturnDoc);

  applyReturnPaymentAmounts(created);

  // Submit it
  const submitted = await submitPosInvoice(created);
  return submitted || created;
}
